<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, ref, type ComputedRef, type Ref } from "vue";
import { useRouter, type Router } from "vue-router";
import type { AuthenticatedSession } from "../api/authentication.js";
import { useAuthenticationStore } from "../stores/authentication.js";
import { useTenantMemberStore } from "../stores/tenant-members.js";
import { useTenantStore } from "../stores/tenants.js";
import { useSystemSettingsStore } from "../stores/system-settings.js";
import ProductMark from "./ProductMark.vue";
import TenantSwitcher from "./TenantSwitcher.vue";

const authenticationStore: ReturnType<typeof useAuthenticationStore> = useAuthenticationStore();
const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const tenantMemberStore: ReturnType<typeof useTenantMemberStore> = useTenantMemberStore();
const systemSettingsStore: ReturnType<typeof useSystemSettingsStore> = useSystemSettingsStore();
const router: Router = useRouter();
const loggingOut: Ref<boolean> = ref(false);
const session: ComputedRef<AuthenticatedSession | null> = computed(
  (): AuthenticatedSession | null => authenticationStore.session,
);

async function performLogout(): Promise<void> {
  if (loggingOut.value) {
    return;
  }

  loggingOut.value = true;
  try {
    await authenticationStore.logout();
  } finally {
    tenantStore.reset();
    tenantMemberStore.reset();
    systemSettingsStore.reset();
    await router.replace({ name: "login" });
    loggingOut.value = false;
  }
}
</script>

<template>
  <header v-if="session" class="app-header">
    <ProductMark />
    <TenantSwitcher />
    <div class="app-header__account">
      <div>
        <strong>{{ session.user.displayName }}</strong>
        <span>{{ session.user.email }}</span>
      </div>
      <button class="button button--quiet" type="button" :disabled="loggingOut" @click="performLogout">
        {{ loggingOut ? "Signing out…" : "Sign out" }}
      </button>
    </div>
  </header>
</template>
